import { queryOptions } from '@tanstack/react-query';
import { quoteBooking } from '@/api/generated/sdk.gen';
import { addDaysYMD } from '@/lib/calendar';
import type { BookingQuote } from '@/api/generated/types.gen';

/** Party composition to price alongside an apartment + date range (OH-65). */
export interface QuoteParty {
  adults: number;
  children: number;
  infants: number;
}

/**
 * Server-computed price for an apartment + date range (the admin quote endpoint,
 * OH-48). Feeds the calendar create-booking flow so the operator sees the real
 * total instead of typing one. Read-only. Callers gate `enabled` on all three
 * inputs being present.
 *
 * `party` (OH-65) is optional — omit it and the request behaves exactly as
 * before (no composition, no extra-guest surcharge). When present it MUST be
 * part of the query key: TanStack Query caches by key, so if the party were
 * left out, changing the number of adults would keep showing the previous
 * (now stale) price instead of triggering a re-quote.
 */
export function bookingQuoteQuery(
  apartmentId: string,
  checkinDate: string,
  checkoutDate: string,
  party?: QuoteParty,
) {
  return queryOptions({
    queryKey: ['bookings', 'quote', { apartmentId, checkinDate, checkoutDate, party }] as const,
    queryFn: async ({ signal }) => {
      const res = await quoteBooking({
        throwOnError: true,
        signal,
        body: {
          apartment_id: apartmentId,
          checkin_date: checkinDate,
          checkout_date: checkoutDate,
          ...(party ? { adults: party.adults, children: party.children, infants: party.infants } : {}),
        },
      });
      return res.data;
    },
    // A stale quote is cheap to refetch and prices can move; keep it short-lived.
    staleTime: 30_000,
    // The quote is read-only/idempotent, so inherit the app's retry policy (a
    // transient blip shouldn't strand pricing). But do NOT refetch on window
    // focus: that would silently recompute the operator's Final total mid-edit.
    refetchOnWindowFocus: false,
  });
}

/**
 * Apply a percentage discount to a minor-unit total, rounded to the nearest
 * minor unit. `pct` outside [0,100] or non-finite → the total is returned
 * unchanged (the caller treats a blank/invalid discount as "no discount").
 */
export function discountedMinor(quoteMinor: number, pct: number): number {
  if (!Number.isFinite(pct) || pct <= 0) return quoteMinor;
  const clamped = Math.min(pct, 100);
  return Math.round(quoteMinor * (1 - clamped / 100));
}

/**
 * Blocked nights, collapsed to readable ranges (OH-139):
 * ['2026-08-04','2026-08-05','2026-08-07'] → ['2026-08-04 – 2026-08-05', '2026-08-07'].
 * Input is assumed ascending — the server emits it that way.
 */
export function collapseYmdRanges(dates: string[]): string[] {
  const out: string[] = [];
  let start: string | null = null;
  let prev: string | null = null;

  const flush = () => {
    if (start === null || prev === null) return;
    out.push(start === prev ? start : `${start} – ${prev}`);
  };

  for (const d of dates) {
    if (start === null) {
      start = d;
    } else if (prev !== null && addDaysYMD(prev, 1) !== d) {
      flush();
      start = d;
    }
    prev = d;
  }
  flush();
  return out;
}

/**
 * The number the form may put in "Final total": the bookable total when the
 * stay is bookable, the indicative total when the stay is blocked but every
 * night carries a rate (OH-139), and null when the server produced no complete
 * price — unpriced nights, over_capacity, or no quote yet. Never a partial sum:
 * a number that silently omits five nights is worse than no number.
 */
export function completePriceMinor(q: BookingQuote | undefined): number | null {
  if (!q) return null;
  if (q.available) return q.total_amount_minor ?? null;
  if ((q.unpriced_nights ?? 0) > 0) return null;
  return q.indicative_total_minor ?? null;
}
